import { useEffect, useMemo, useState } from "react";
import { launchReadinessStatuses } from "@/features/tools/launchReadinessQuestions";
import type {
  Blueprint,
  LaunchReadinessChecklistItem,
  LaunchReadinessChecklistRequest,
  LaunchReadinessChecklistResponse,
  LaunchReadinessCompletedCheck,
  LaunchReadinessRequest,
  LaunchReadinessStatus,
} from "@/features/tools/proToolTypes";
import type { Project } from "@/features/projects/projectTypes";

type GeneratedBlueprintOption = Pick<
  Blueprint,
  "id" | "project_id" | "title" | "target_builder" | "created_at" | "updated_at"
>;

type LaunchReadinessFormProps = {
  projects: Project[];
  generatedBlueprints: GeneratedBlueprintOption[];
  isGeneratingChecklist: boolean;
  isSubmitting: boolean;
  error: string | null;
  onGenerateChecklist: (
    input: LaunchReadinessChecklistRequest,
  ) => Promise<LaunchReadinessChecklistResponse>;
  onSubmit: (input: LaunchReadinessRequest) => void;
};

type CheckState = LaunchReadinessChecklistItem & {
  status: LaunchReadinessStatus;
  note?: string;
};

type InputMode = "blueprint" | "manual";

const MANUAL_DESCRIPTION_MIN_LENGTH = 40;
const MANUAL_DESCRIPTION_HELPER =
  "Describe what the app does, who it helps, and the problem it solves.";
const MANUAL_DESCRIPTION_ERROR =
  "Please add a bit more detail so the checker can create meaningful readiness checks.";
const MANUAL_DESCRIPTION_EXAMPLE =
  "Example: A simple app that helps coaches track client goals, weekly actions, progress notes, and next steps.";

const vagueManualDescriptions = new Set([
  "app",
  "an app",
  "just app",
  "simple app",
  "my app",
  "test app",
  "app idea",
]);

function getInitialMode(blueprints: GeneratedBlueprintOption[]): InputMode {
  return blueprints.length > 0 ? "blueprint" : "manual";
}

function getInitialProjectId(
  projects: Project[],
  blueprints: GeneratedBlueprintOption[],
) {
  return blueprints[0]?.project_id ?? projects[0]?.id ?? "";
}

function formatBuilder(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isManualDescriptionMeaningful(description: string) {
  const normalized = description.trim().toLowerCase().replace(/\s+/g, " ");

  return (
    normalized.length >= MANUAL_DESCRIPTION_MIN_LENGTH &&
    !vagueManualDescriptions.has(normalized)
  );
}

export function LaunchReadinessForm({
  projects,
  generatedBlueprints,
  isGeneratingChecklist,
  isSubmitting,
  error,
  onGenerateChecklist,
  onSubmit,
}: LaunchReadinessFormProps) {
  const [inputMode, setInputMode] = useState<InputMode>(() =>
    getInitialMode(generatedBlueprints),
  );
  const [hasChosenMode, setHasChosenMode] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(() =>
    getInitialProjectId(projects, generatedBlueprints),
  );
  const [selectedBlueprintId, setSelectedBlueprintId] = useState("");
  const [appDescription, setAppDescription] = useState("");
  const [checks, setChecks] = useState<CheckState[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredBlueprints = useMemo(
    () =>
      generatedBlueprints.filter(
        (blueprint) => blueprint.project_id === selectedProjectId,
      ),
    [generatedBlueprints, selectedProjectId],
  );

  useEffect(() => {
    if (!hasChosenMode && inputMode === "manual" && generatedBlueprints.length > 0) {
      setInputMode("blueprint");
      return;
    }

    if (
      !selectedProjectId &&
      (projects.length > 0 || generatedBlueprints.length > 0)
    ) {
      setSelectedProjectId(getInitialProjectId(projects, generatedBlueprints));
    }
  }, [
    generatedBlueprints,
    hasChosenMode,
    inputMode,
    projects,
    selectedProjectId,
  ]);

  useEffect(() => {
    if (inputMode !== "blueprint") {
      return;
    }

    const selectedStillAvailable = filteredBlueprints.some(
      (blueprint) => blueprint.id === selectedBlueprintId,
    );

    if (!selectedStillAvailable) {
      setSelectedBlueprintId(filteredBlueprints[0]?.id ?? "");
    }
  }, [filteredBlueprints, inputMode, selectedBlueprintId]);

  const selectedBlueprint = useMemo(
    () =>
      filteredBlueprints.find(
        (blueprint) => blueprint.id === selectedBlueprintId,
      ),
    [filteredBlueprints, selectedBlueprintId],
  );

  const canGenerateChecklist = useMemo(() => {
    if (inputMode === "blueprint") {
      return Boolean(selectedBlueprint);
    }

    return isManualDescriptionMeaningful(appDescription);
  }, [appDescription, inputMode, selectedBlueprint]);

  const manualDescriptionState = useMemo(() => {
    if (inputMode !== "manual") {
      return null;
    }

    if (!appDescription.trim()) {
      return {
        tone: "helper" as const,
        message: MANUAL_DESCRIPTION_HELPER,
      };
    }

    if (!isManualDescriptionMeaningful(appDescription)) {
      return {
        tone: "error" as const,
        message: MANUAL_DESCRIPTION_ERROR,
      };
    }

    return {
      tone: "helper" as const,
      message: "This is enough detail to generate a useful checklist.",
    };
  }, [appDescription, inputMode]);

  const completedChecks = useMemo<LaunchReadinessCompletedCheck[]>(
    () =>
      checks.map((check) => ({
        id: check.id,
        category: check.category,
        question: check.question,
        whyItMatters: check.whyItMatters,
        sourceHint: check.sourceHint,
        status: check.status,
        note: check.note?.trim() || undefined,
      })),
    [checks],
  );

  async function handleGenerateChecklist() {
    setLocalError(null);

    if (!canGenerateChecklist) {
      setLocalError(
        inputMode === "manual"
          ? MANUAL_DESCRIPTION_ERROR
          : "Select a generated blueprint before generating a readiness checklist.",
      );
      return;
    }

    try {
      const response = await onGenerateChecklist({
        blueprintId:
          inputMode === "blueprint" ? selectedBlueprint?.id : undefined,
        projectId:
          inputMode === "blueprint"
            ? selectedBlueprint?.project_id
            : undefined,
        appDescription,
      });

      setChecks(
        response.checks.map((check) => ({
          ...check,
          status: "partly_working",
        })),
      );
    } catch (err) {
      setLocalError(
        err instanceof Error
          ? err.message
          : "We could not generate this right now. Please try again.",
      );
    }
  }

  function updateStatus(checkId: string, status: LaunchReadinessStatus) {
    setChecks((currentChecks) =>
      currentChecks.map((check) =>
        check.id === checkId ? { ...check, status } : check,
      ),
    );
  }

  function updateNote(checkId: string, note: string) {
    setChecks((currentChecks) =>
      currentChecks.map((check) =>
        check.id === checkId ? { ...check, note } : check,
      ),
    );
  }

  return (
    <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div>
        <p className="text-sm font-semibold text-indigo-700">
          Launch Readiness Checker
        </p>
        <h3 className="mt-2 text-xl font-semibold text-slate-950">
          Check a generated blueprint before launch
        </h3>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Select one of your generated blueprints, generate a feature-specific
          launch checklist, then mark what is working, tested, or still missing.
        </p>
      </div>

      {error || localError ? (
        <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {localError ?? error}
        </div>
      ) : null}

      <div className="mt-6">
        <div className="flex w-full flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-1 sm:w-auto">
          {(
            [
              ["blueprint", "Use generated blueprint"],
              ["manual", "Describe manually"],
            ] as Array<[InputMode, string]>
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setInputMode(mode);
                setHasChosenMode(true);
                setChecks([]);
                setLocalError(null);
              }}
              className={`flex-1 whitespace-normal rounded-md px-3 py-2 text-sm font-semibold transition sm:flex-none ${
                inputMode === mode
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-950"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="min-w-0">
          {inputMode === "blueprint" ? (
            <div className="grid min-w-0 gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <label
                  htmlFor="launch-readiness-project"
                  className="block text-sm font-medium text-slate-700"
                >
                  Project
                </label>
                <select
                  id="launch-readiness-project"
                  value={selectedProjectId}
                  onChange={(event) => {
                    setSelectedProjectId(event.target.value);
                    setSelectedBlueprintId("");
                    setChecks([]);
                  }}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No projects found</option>
                  )}
                </select>
              </div>

              <div className="min-w-0">
                <label
                  htmlFor="launch-readiness-blueprint"
                  className="block text-sm font-medium text-slate-700"
                >
                  Generated blueprint
                </label>
                <select
                  id="launch-readiness-blueprint"
                  value={selectedBlueprintId}
                  onChange={(event) => {
                    setSelectedBlueprintId(event.target.value);
                    setChecks([]);
                  }}
                  disabled={filteredBlueprints.length === 0}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                >
                  {filteredBlueprints.length > 0 ? (
                    filteredBlueprints.map((blueprint) => (
                      <option key={blueprint.id} value={blueprint.id}>
                        {blueprint.title}
                      </option>
                    ))
                  ) : (
                    <option value="">
                      This project does not have a generated blueprint yet.
                    </option>
                  )}
                </select>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-500">
                  Choose the project first, then select the generated blueprint
                  you want to check.
                </p>
                {selectedBlueprint ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Target builder:{" "}
                    {formatBuilder(selectedBlueprint.target_builder)}
                  </p>
                ) : filteredBlueprints.length === 0 ? (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    This project does not have a generated blueprint yet.
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
              Manual input is available when you want to check an app that
              does not have a generated blueprint yet. Add enough detail below
              so the checklist can be specific.
            </div>
          )}
        </div>

        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm leading-6 text-indigo-950">
          <p className="font-semibold">Blueprint-first by default</p>
          <p className="mt-1">
            Manual descriptions work best with enough detail to generate
            meaningful checks.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <label
          htmlFor="launch-readiness-description"
          className="block text-sm font-medium text-slate-700"
        >
          {inputMode === "blueprint"
            ? "Optional extra context"
            : "Manual app description"}
        </label>
        <textarea
          id="launch-readiness-description"
          value={appDescription}
          onChange={(event) => {
            setAppDescription(event.target.value);
            setLocalError(null);
          }}
          maxLength={4000}
          rows={5}
          className="mt-2 w-full min-w-0 max-w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          placeholder={
            inputMode === "blueprint"
              ? "Optional: add anything about the build that is not in the blueprint yet."
              : "Describe the app, target user, core workflow, main screens, and launch goal in at least 40 characters."
          }
        />
        <p className="mt-2 text-xs text-slate-500">
          {appDescription.length} / 4000
        </p>
        {manualDescriptionState ? (
          <div
            className={`mt-2 rounded-md border p-3 text-sm leading-6 ${
              manualDescriptionState.tone === "error"
                ? "border-amber-200 bg-amber-50 text-amber-900"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            <p>{manualDescriptionState.message}</p>
            <p className="mt-1">{MANUAL_DESCRIPTION_EXAMPLE}</p>
          </div>
        ) : null}
      </div>

      <div className="mt-6 flex justify-stretch sm:justify-end">
        <button
          type="button"
          onClick={() => void handleGenerateChecklist()}
          disabled={!canGenerateChecklist || isGeneratingChecklist}
          className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto"
        >
          {isGeneratingChecklist
            ? "Creating your launch readiness checklist..."
            : "Generate readiness checklist"}
        </button>
      </div>

      {checks.length > 0 ? (
        <div className="mt-8">
          <div>
            <h4 className="text-lg font-semibold text-slate-950">
              Blueprint-specific checks
            </h4>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Mark the real build status for each item. The final score is based
              on these statuses, not a generic rating.
            </p>
          </div>

          <div className="mt-5 grid gap-4">
            {checks.map((check) => (
              <div
                key={check.id}
                className="rounded-lg border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                      {check.category}
                    </p>
                    <p className="mt-2 break-words text-sm font-semibold text-slate-950">
                      {check.question}
                    </p>
                  </div>
                  <p className="rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-500">
                    {check.sourceHint}
                  </p>
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {check.whyItMatters}
                </p>

                <div className="mt-4 grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {launchReadinessStatuses.map((status) => {
                    const isSelected = check.status === status.value;

                    return (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => updateStatus(check.id, status.value)}
                        className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                          isSelected
                            ? "border-indigo-600 bg-indigo-600 text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <span className="block font-semibold">
                          {status.label}
                        </span>
                        <span
                          className={`mt-1 block text-xs leading-5 ${
                            isSelected ? "text-indigo-100" : "text-slate-500"
                          }`}
                        >
                          {status.description}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <label
                  htmlFor={`launch-readiness-note-${check.id}`}
                  className="mt-4 block text-sm font-medium text-slate-700"
                >
                  Optional note
                </label>
                <input
                  id={`launch-readiness-note-${check.id}`}
                  value={check.note ?? ""}
                  onChange={(event) => updateNote(check.id, event.target.value)}
                  maxLength={500}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder="Add context if this check needs explanation."
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-stretch sm:justify-end">
            <button
              type="button"
              onClick={() =>
                onSubmit({
                  blueprintId:
                    inputMode === "blueprint"
                      ? selectedBlueprint?.id
                      : undefined,
                  projectId:
                    inputMode === "blueprint"
                      ? selectedBlueprint?.project_id
                      : undefined,
                  appDescription,
                  checks: completedChecks,
                })
              }
              disabled={isSubmitting}
              className="w-full rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 sm:w-auto"
            >
              {isSubmitting ? "Checking..." : "Check launch readiness"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
